/**
 * Action Center alert list (2026-09-16 redesign): which Firestore docs become cards, what each card
 * says, and the chips / focus filter / sort / Today-Earlier grouping the panel renders.
 *
 * Pure — the panel component owns Firestore and routing; this file turns raw `bot-events` docs and
 * computed SMS alerts into view models. Node-tests execute it directly
 * (cypress/node-tests/action-center-presenters.test.js, action-center-grouping.test.js).
 *
 * Card kinds follow the Claude Design "KIND" table (Downloads/Teamio Action Center redesign), with
 * the decisions confirmed 2026-09-16: form name only (never form type), plan name + date only (no
 * totals or codes), online booking time is office wall clock, remote payments only.
 */
import { coerceToMs } from './firestoreTime';
import { isBotEventCleared, isBotEventResolved } from './actionCenterReadState';
import {
  formatDateTime, formatRelativeAge, zonedDayKey, formatBookingAppointment, dayDiff,
} from './actionCenterTime';
import { buildSendNewLinkRoute, buildViewPaymentRoute } from './actionCenterPaymentLink';

export const CATEGORIES = [
  { key: 'all', label: 'All', icon: 'inbox' },
  { key: 'sms', label: 'SMS', icon: 'message-square' },
  { key: 'forms', label: 'Forms', icon: 'file-text' },
  { key: 'tx', label: 'Tx', icon: 'file-check' },
  { key: 'appts', label: 'Appts', icon: 'calendar-check' },
  { key: 'pay', label: 'Payments', icon: 'credit-card' },
];

export const EVENT_TYPES = {
  FORM_SUBMISSION: 'FORM_SUBMISSION',
  TX_PLAN_SIGNED: 'TX_PLAN_SIGNED',
  TX_PLAN_QUESTION: 'TX_PLAN_QUESTION',
  ONLINE_BOOKING_CREATED: 'ONLINE_BOOKING_CREATED',
  PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
};

// Allowlist, not a blocklist: leftover BROKEN_APPOINTMENT docs (alerts removed 2026-09-16) and any
// future type an older bundle doesn't understand must never render as a raw-label card.
export const ALERT_EVENT_TYPES = Object.keys(EVENT_TYPES).map((k) => EVENT_TYPES[k]);

// `testId` becomes the card's data-test attribute (docs screenshots and e2e specs target it).
export const KINDS = {
  smsUnanswered: { label: 'Unanswered SMS', tone: 'red', category: 'sms', needsAction: true, testId: 'ac-card-sms' },
  smsUnread: { label: 'Unread SMS', tone: 'orange', category: 'sms', needsAction: true, testId: 'ac-card-sms' },
  form: { label: 'Form submitted', tone: 'blue', category: 'forms', needsAction: false, testId: 'ac-card-form' },
  packet: { label: 'Packet in progress', tone: 'blue', category: 'forms', needsAction: false, testId: 'ac-card-packet' },
  packetDone: { label: 'Packet submitted', tone: 'blue', category: 'forms', needsAction: false, testId: 'ac-card-packet' },
  txSigned: { label: 'Treatment plan signed', tone: 'iris', category: 'tx', needsAction: false, testId: 'ac-card-tx-signed' },
  txAsk: { label: 'Question on plan', tone: 'purple', category: 'tx', needsAction: true, testId: 'ac-card-tx-question' },
  booking: { label: 'New online booking', tone: 'amber', category: 'appts', needsAction: false, testId: 'ac-card-booking' },
  paid: { label: 'Payment received', tone: 'green', category: 'pay', needsAction: false, testId: 'ac-card-pay-received' },
  payFail: { label: 'Payment failed', tone: 'red', category: 'pay', needsAction: true, testId: 'ac-card-pay-failed' },
};

export const FOCUS_FILTERS = {
  all: { label: 'Needs action', icon: 'zap' },
  sms: { label: 'Over 12h old', icon: 'clock' },
  forms: { label: 'Packets only', icon: 'layers' },
  tx: { label: 'Questions only', icon: 'message-circle' },
  appts: { label: 'Next 7 days', icon: 'calendar-clock' },
  pay: { label: 'Failed only', icon: 'alert-triangle' },
};

export const EMPTY_STATES = {
  all: { icon: 'inbox', title: 'Inbox zero', body: 'Nothing waiting on the team. New texts, forms, plans, bookings and payments land here as they come in.' },
  sms: { icon: 'message-square', title: 'No unanswered texts', body: 'Every patient message has a reply. Nice work.' },
  forms: { icon: 'file-text', title: 'No new submissions', body: 'Forms show up here the moment a patient submits one.' },
  tx: { icon: 'file-check', title: 'No plan activity', body: 'Signatures and patient questions on treatment plans will appear here.' },
  appts: { icon: 'calendar-check', title: 'No new bookings', body: 'Appointments booked through online scheduling will appear here.' },
  pay: { icon: 'credit-card', title: 'No new payments', body: 'Payments made through text-to-pay links land here, including any that fail.' },
};

const FOCUS_EMPTY = { icon: 'filter', title: 'Nothing matches this filter', body: 'Turn the filter off to see everything in this list.' };

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

function text(v) {
  return v === null || v === undefined ? '' : String(v).trim();
}

function firstMs(...values) {
  for (const v of values) {
    const ms = coerceToMs(v);
    if (ms) return ms;
  }
  return null;
}

export function formatMoney(amount) {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

const CARD_BRANDS = { visa: 'Visa', mastercard: 'Mastercard', mc: 'Mastercard', amex: 'Amex', americanexpress: 'Amex', discover: 'Discover', jcb: 'JCB', dinersclub: 'Diners Club', diners: 'Diners Club' };

export function formatCardMethod(brand, last4) {
  const key = text(brand).toLowerCase().replace(/[^a-z]/g, '');
  const name = CARD_BRANDS[key] || (text(brand) ? text(brand).charAt(0).toUpperCase() + text(brand).slice(1) : '');
  const digits = text(last4).replace(/\D/g, '').slice(-4);
  if (name && digits) return `${name} ···· ${digits}`;
  if (digits) return `Card ···· ${digits}`;
  return name;
}

export function paymentIntentLabel(intent) {
  if (intent === 'prepayment') return 'Treatment plan prepayment';
  if (intent === 'balance') return 'Account balance';
  return 'Payment';
}

function formSubmissionPath(submissionID) {
  return submissionID ? `/app/forms/view-submission/${submissionID}` : null;
}

// Pre-redesign FORM_SUBMISSION docs have no formSubmissionIDForWeb in the payload — only the
// VIEW_FORM action url.
function legacySubmissionID(evt) {
  const actions = Array.isArray(evt && evt.actions) ? evt.actions : [];
  const view = actions.find((a) => a && a.type === 'VIEW_FORM' && typeof a.url === 'string');
  const m = view && /\/app\/forms\/view-submission\/([^/?#]+)/.exec(view.url);
  return m && m[1] !== 'undefined' && m[1] !== 'null' ? decodeURIComponent(m[1]) : null;
}

function patientProfileRoute(siteID, patNum) {
  return siteID && patNum ? { name: 'patientprofile', params: { patientID: `${siteID}-${patNum}` } } : null;
}

function planBuilderPath(patNum, treatPlanNum) {
  return patNum && treatPlanNum ? `/app/tx-presenter/builder/${patNum}/${treatPlanNum}` : null;
}

function buildItem(kindKey, { id, source, title, subtitle, quote, meta, displayAt, sortAt, primary, secondary, reply, extra }, { timeZone, now }) {
  const kind = KINDS[kindKey];
  const displayMs = displayAt || sortAt || null;
  return {
    key: `${source}:${id}`,
    id,
    source,
    kind: kindKey,
    testId: kind.testId,
    category: kind.category,
    tone: kind.tone,
    label: kind.label,
    needsAction: kind.needsAction,
    title: text(title) || 'Unknown patient',
    subtitle: text(subtitle),
    quote: text(quote),
    meta: (meta || []).filter((row) => row && text(row.v)),
    displayAt: displayMs,
    sortAt: sortAt || displayMs || 0,
    dayKey: zonedDayKey(displayMs, timeZone),
    time: formatDateTime(displayMs, timeZone),
    age: formatRelativeAge(displayMs, now),
    primary: primary || null,
    secondary: secondary || { action: 'dismiss', label: 'Dismiss' },
    reply: !!reply,
    ...(extra || {}),
  };
}

/** A computed SMS alert (actionCenterSms.computeMissedSmsAlerts) → card. */
export function presentSmsAlert(alert, { timeZone, now } = {}) {
  if (!alert || !alert.conversationSID) return null;
  const at = alert.lastMessageTime || null;
  return buildItem(alert.type === 'unread' ? 'smsUnread' : 'smsUnanswered', {
    id: alert.conversationSID,
    source: 'sms',
    title: alert.patientName,
    subtitle: alert.phone,
    quote: alert.lastMessageBody,
    meta: [{ k: 'Received', v: formatDateTime(at, timeZone) }],
    displayAt: at,
    sortAt: at,
    primary: { action: 'navigate', label: 'View conversation', to: { name: 'conversations', query: { roomId: alert.conversationSID } } },
    secondary: { action: 'dismiss', label: 'Dismiss' },
    reply: true,
    extra: { conversationSID: alert.conversationSID, phone: text(alert.phone), smsType: alert.type },
  }, { timeZone, now });
}

export function kindForBotEvent(evt) {
  if (!evt) return null;
  switch (evt.eventType) {
    case EVENT_TYPES.FORM_SUBMISSION:
      if (!evt.isPacket) return 'form';
      return evt.payload && evt.payload.isComplete ? 'packetDone' : 'packet';
    case EVENT_TYPES.TX_PLAN_SIGNED: return 'txSigned';
    case EVENT_TYPES.TX_PLAN_QUESTION: return 'txAsk';
    case EVENT_TYPES.ONLINE_BOOKING_CREATED: return 'booking';
    case EVENT_TYPES.PAYMENT_RECEIVED: return 'paid';
    case EVENT_TYPES.PAYMENT_FAILED: return 'payFail';
    default: return null;
  }
}

function packetSteps(payload, timeZone) {
  const submissions = payload && payload.submissions && typeof payload.submissions === 'object' ? payload.submissions : {};
  return Object.keys(submissions)
    .map((k) => ({ step: Number(k), row: submissions[k] || {} }))
    .filter((s) => Number.isInteger(s.step) && s.step > 0)
    .sort((a, b) => a.step - b.step)
    .map(({ step, row }) => {
      const at = coerceToMs(row.submittedAt);
      return {
        step,
        formName: text(row.formName) || `Form ${step}`,
        submittedAt: at,
        time: formatDateTime(at, timeZone),
        to: formSubmissionPath(text(row.formSubmissionIDForWeb)),
      };
    });
}

/**
 * A `bot-events` doc → card, or null when it isn't an Action Center alert type.
 * `canViewPayments`: the viewer can open My Account → Patient Payments (actionCenterPaymentLink
 * .canViewPatientPaymentsPage) — decides "View payment" vs "View patient" on a received payment.
 */
export function presentBotEvent(evt, { timeZone, now, canViewPayments = false } = {}) {
  const kindKey = kindForBotEvent(evt);
  if (!kindKey) return null;
  const p = evt.payload || {};
  const sortAt = firstMs(evt.sortAt, evt.createdAt);
  const ctx = { timeZone, now };
  const common = { id: evt.id, source: 'event', title: p.patientName };
  const markRead = { action: 'dismiss', label: 'Mark read' };

  if (kindKey === 'form') {
    const submissionID = text(p.formSubmissionIDForWeb) || legacySubmissionID(evt);
    const at = firstMs(evt.displayAt, p.submittedAt, evt.createdAt);
    return buildItem(kindKey, {
      ...common,
      subtitle: text(p.formName) || 'Form',
      meta: [{ k: 'Submitted', v: formatDateTime(at, timeZone) }],
      displayAt: at,
      sortAt,
      primary: submissionID ? { action: 'navigate', label: 'View form', to: formSubmissionPath(submissionID) } : null,
      secondary: markRead,
    }, ctx);
  }

  if (kindKey === 'packet' || kindKey === 'packetDone') {
    const done = kindKey === 'packetDone';
    const total = Number(p.total) || 0;
    const completed = Number(p.completedCount) || 0;
    const name = text(p.packetName) || 'Forms packet';
    const steps = packetSteps(p, timeZone);
    const latest = steps.filter((s) => s.to).sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0))[0];
    const at = done
      ? firstMs(p.completedAt, evt.displayAt, p.lastSubmittedAt, evt.createdAt)
      : firstMs(evt.displayAt, p.lastSubmittedAt, evt.createdAt);
    const meta = done
      ? [{ k: 'Submitted', v: formatDateTime(at, timeZone) }]
      : [
        { k: 'Started', v: formatDateTime(coerceToMs(p.startedAt), timeZone) },
        { k: 'Last form', v: formatDateTime(at, timeZone) },
      ];
    return buildItem(kindKey, {
      ...common,
      subtitle: done
        ? `${name} · ${total} ${total === 1 ? 'form' : 'forms'}`
        : `${name} · ${completed} of ${total} forms`,
      meta,
      displayAt: at,
      sortAt,
      primary: latest ? { action: 'navigate', label: 'View latest form', to: latest.to } : null,
      secondary: markRead,
      extra: { steps, packetProgress: { completed, total } },
    }, ctx);
  }

  if (kindKey === 'txSigned') {
    const at = firstMs(evt.displayAt, p.signedAt, evt.createdAt);
    const to = planBuilderPath(p.patNum, p.treatPlanNum);
    return buildItem(kindKey, {
      ...common,
      subtitle: text(p.planHeading) || 'Treatment plan',
      meta: [{ k: 'Signed', v: formatDateTime(at, timeZone) }],
      displayAt: at,
      sortAt,
      primary: to ? { action: 'navigate', label: 'View plan', to } : null,
    }, ctx);
  }

  if (kindKey === 'txAsk') {
    const at = firstMs(evt.displayAt, p.askedAt, evt.createdAt);
    const to = planBuilderPath(p.patNum, p.treatPlanNum);
    const heldDraft = Number(p.heldAiCommentID) > 0 ? Number(p.heldAiCommentID) : null;
    return buildItem(kindKey, {
      ...common,
      subtitle: text(p.planHeading) || 'Treatment plan',
      quote: p.question,
      meta: [{ k: 'Asked', v: formatDateTime(at, timeZone) }],
      displayAt: at,
      sortAt,
      primary: to ? { action: 'navigate', label: 'View plan', to } : null,
      reply: !!(p.patNum && p.treatPlanNum),
      extra: {
        note: heldDraft ? 'An AI draft reply is waiting for review. Replying here sends yours instead.' : '',
        txQuestion: {
          patNum: p.patNum,
          treatPlanNum: p.treatPlanNum,
          commentID: p.commentID,
          heldAiCommentID: heldDraft,
          aiReplyMode: p.aiReplyMode || 'off',
        },
      },
    }, ctx);
  }

  if (kindKey === 'booking') {
    const at = firstMs(evt.displayAt, p.bookedAt, evt.createdAt);
    const nowYear = zonedDayKey(now, timeZone).slice(0, 4);
    const duration = Number(p.durationMins) > 0 ? `${Number(p.durationMins)} min` : '';
    return buildItem(kindKey, {
      ...common,
      subtitle: [text(p.appointmentTypeName) || 'Appointment', duration, text(p.providerName)].filter(Boolean).join(' · '),
      meta: [
        { k: 'Appointment', v: formatBookingAppointment(p.appointmentDate, p.appointmentTime, { currentYear: nowYear }) },
        { k: 'Patient', v: p.patientType === 'new' ? 'New patient' : 'Returning patient' },
        { k: 'Booked online', v: formatDateTime(at, timeZone) },
      ],
      displayAt: at,
      sortAt,
      primary: { action: 'navigate', label: 'View online bookings', to: { name: 'OnlineAppointments' } },
      extra: { appointmentDate: text(p.appointmentDate) },
    }, ctx);
  }

  if (kindKey === 'paid') {
    const at = firstMs(evt.displayAt, p.receivedAt, evt.createdAt);
    // Admins with Open Dental write-back see My Account → Patient Payments; for everyone else that page
    // is a placeholder, so they get the patient's profile.
    const paymentsPage = canViewPayments
      ? buildViewPaymentRoute({ dayKey: zonedDayKey(at, timeZone), patientName: p.patientName })
      : null;
    const profile = patientProfileRoute(evt.siteID, p.patNum);
    let primary = null;
    if (paymentsPage) primary = { action: 'navigate', label: 'View payment', to: paymentsPage };
    else if (profile) primary = { action: 'navigate', label: 'View patient', to: profile };
    return buildItem(kindKey, {
      ...common,
      subtitle: paymentIntentLabel(p.intent),
      meta: [
        { k: 'Amount', v: formatMoney(p.amount) },
        { k: 'Method', v: formatCardMethod(p.cardBrand, p.cardLast4) },
        { k: 'Received', v: formatDateTime(at, timeZone) },
      ],
      displayAt: at,
      sortAt,
      primary,
    }, ctx);
  }

  // payFail
  const at = firstMs(evt.displayAt, p.attemptedAt, evt.createdAt);
  const sendNewLinkTo = buildSendNewLinkRoute(evt.siteID, p);
  return buildItem(kindKey, {
    ...common,
    subtitle: paymentIntentLabel(p.intent),
    meta: [
      { k: 'Amount', v: formatMoney(p.amount) },
      { k: 'Reason', v: text(p.declineReason) || 'Payment was not approved' },
      { k: 'Attempted', v: formatDateTime(at, timeZone) },
    ],
    displayAt: at,
    sortAt,
    // Opens the patient's Collect Payment dialog pre-filled — the operator reviews and sends from there.
    primary: sendNewLinkTo ? { action: 'sendNewLink', label: 'Send new link', to: sendNewLinkTo } : null,
    extra: {
      failedPayment: {
        paymentID: p.paymentID,
        patNum: p.patNum,
        patientName: text(p.patientName),
        patientPhone: text(p.patientPhone),
        amount: p.amount,
        intent: p.intent,
        treatPlanNum: p.treatPlanNum || null,
      },
    },
  }, ctx);
}

function isPinned(pinned, id) {
  if (!pinned) return false;
  return typeof pinned.has === 'function' ? pinned.has(id) : !!pinned[id];
}

/**
 * Everything the viewer should see, unsorted.
 * `smsAlerts` are already filtered for dismissal by computeMissedSmsAlerts; bot events are filtered
 * here. `pinned` = ids of cards with an open quick-reply draft, kept even if a teammate clears them.
 */
export function buildAlertItems({ smsAlerts, botEvents, employeeID, scope, pinned, timeZone, now, canViewPayments = false } = {}) {
  const ctx = { timeZone, now, canViewPayments };
  const items = [];
  (smsAlerts || []).forEach((a) => {
    const item = presentSmsAlert(a, ctx);
    if (item) items.push(item);
  });
  (botEvents || []).forEach((evt) => {
    if (!evt || !kindForBotEvent(evt)) return;
    // Handled (question answered, new link sent) hides the card even mid-draft: the reply is no longer needed.
    if (isBotEventResolved(evt)) return;
    if (!isPinned(pinned, evt.id) && isBotEventCleared(evt, { employeeID, scope })) return;
    const item = presentBotEvent(evt, ctx);
    if (item) items.push(item);
  });
  return items;
}

export function countByCategory(items) {
  const counts = { all: 0, sms: 0, forms: 0, tx: 0, appts: 0, pay: 0 };
  (items || []).forEach((item) => {
    counts.all += 1;
    if (Object.prototype.hasOwnProperty.call(counts, item.category)) counts[item.category] += 1;
  });
  return counts;
}

/** Does the card pass the focus filter of the chip it's shown under? */
export function matchesFocus(item, category, { now, timeZone } = {}) {
  switch (category) {
    case 'sms': return !!item.displayAt && now - item.displayAt >= TWELVE_HOURS_MS;
    case 'forms': return item.kind === 'packet' || item.kind === 'packetDone';
    case 'tx': return item.kind === 'txAsk';
    case 'appts': {
      if (item.kind !== 'booking') return false;
      const diff = dayDiff(zonedDayKey(now, timeZone), item.appointmentDate);
      return diff !== null && diff >= 0 && diff <= 7;
    }
    case 'pay': return item.kind === 'payFail';
    default: return !!item.needsAction;
  }
}

export function filterItems(items, { category = 'all', focus = false, now, timeZone } = {}) {
  const cat = FOCUS_FILTERS[category] ? category : 'all';
  return (items || [])
    .filter((item) => cat === 'all' || item.category === cat)
    .filter((item) => !focus || matchesFocus(item, cat, { now, timeZone }));
}

/** 'new' = newest first (default), 'old' = oldest first. Ties break on key so order is stable. */
export function sortItems(items, order = 'new') {
  const dir = order === 'old' ? 1 : -1;
  return (items || []).slice().sort((a, b) => {
    const diff = (a.sortAt || 0) - (b.sortAt || 0);
    if (diff) return diff * dir;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

/** Today / Earlier by the practice's calendar day. Empty groups are omitted; input order is kept. */
export function groupItemsByDay(items, { timeZone, now } = {}) {
  const today = zonedDayKey(now, timeZone);
  const groups = [
    { key: 'today', label: 'Today', items: [] },
    { key: 'earlier', label: 'Earlier', items: [] },
  ];
  (items || []).forEach((item) => {
    groups[item.dayKey && item.dayKey === today ? 0 : 1].items.push(item);
  });
  return groups.filter((g) => g.items.length);
}

export function emptyStateFor(category, { focus = false } = {}) {
  if (focus) return FOCUS_EMPTY;
  return EMPTY_STATES[category] || EMPTY_STATES.all;
}

export function summaryText(count) {
  if (!count) return 'Nothing waiting';
  return `${count} ${count === 1 ? 'item' : 'items'} waiting`;
}
